import React, { useEffect, useState } from 'react';
import { WrappedFormUtils } from '@ant-design/compatible/lib/form/Form';
import dayjs from 'dayjs';
import Form from '../../../../../components/form/Form';
import Panel from '../../../../../components/Panel';
import Select from '../../../../../components/input/Select';
import {
  WIDGET_TYPE_LABEL_V2,
  WIDGET_TYPE_SELECT_LIST_V2,
} from '../../../../../constants/service/app/widgetConstantsV2';
import { WIDGET_TYPE_V2, EventWidgetDetailV2, EventWidgetRequestPayloadV2 } from '../../../../../types/widget';
import WidgetLayoutSelect from '../../common/components/WidgetLayoutSelect';
import Notice from '../../../../../components/Notice';
import Input from '../../../../../components/input/Input/Input';
import TextArea from '../../../../../components/input/TextArea/TextArea';
import { DateRangePicker } from '../../../../../components/input/RangePicker';
import { DATE_FORMAT } from '../../../../../types/common/date';
import FormListTab from '../../../../../components/input/FormListTab/FormListTab';
import LinkItem from '../../../../../components/input/LinkItem';
import ImageAndVideoUploadV2 from '../../../../../components/input/ImageAndVideoUploadV2';
import { useEventWidgetFormRules } from './useEventWidgetFormRules';
import { WIDGET } from '../../../../../constants/queryKeys';
import { eventReactTableColumns } from '../../common/columns';
import { eventListFetcher } from '../../common/fetchers/eventListFetcher';
import SearchListTrigger from '../../../../../components/SearchListTrigger';
import { useEventWidgetForm } from './useEventWidgetForm';
import EventWidgetListTable from './EventWidgetListTable';
import FormWithTab from '../../../../../components/form/Form/FormWithTab';
import { FieldError } from '../../../../../types/common/form';
import WidgetFormFooter from '../../WidgetFormFooter';
import EventWidgetPreviewModal from './EventWidgetPreviewModal/EventWidgetPreviewModal';
import { getInitialPeriod } from '../../common/utils/getInitialPeriod';
import { textNormalize } from '../../../../../lib/utils/form/normalize';

interface EventWidgetFormProps {
  isEdit: boolean;
  form: WrappedFormUtils;
  initialValue: Partial<EventWidgetDetailV2>;
  onChangeType: (type: WIDGET_TYPE_V2) => void;
  onFinish: (value: EventWidgetRequestPayloadV2) => void;
}

const EventWidgetForm = (props: EventWidgetFormProps): JSX.Element => {
  const { isEdit, initialValue, onFinish, form, onChangeType } = props;
  const [currentTab, setCurrentTab] = useState<number>();

  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);

  useEffect(() => {
    if (!currentTab && initialValue.widgetItemEvents && initialValue.widgetItemEvents.length)
      setCurrentTab(initialValue.widgetItemEvents[0].id);
  }, [initialValue]);

  const { widgetItemEvents, eventWidgetList, handleEventListDragEnd } = useEventWidgetForm(form);
  const formRule = useEventWidgetFormRules(form);

  const handleFinish = (value: EventWidgetDetailV2) => onFinish(new EventWidgetRequestPayloadV2(value));

  const handleFinishFailed = (errors: FieldError[]) => {
    const tabError = errors.find((err) => err.field === 'widgetItemEvents');
    const firstErrorTab = tabError?.errors?.length && tabError?.errors?.[0].id;
    if (firstErrorTab) setCurrentTab(firstErrorTab);
  };

  return (
    <FormWithTab
      name="widgetItemEvents"
      form={form}
      onFinish={handleFinish}
      initialValue={initialValue}
      onFinishFailed={handleFinishFailed}
    >
      <Panel title="위젯 정보">
        <Panel.Section>
          <Form.Item
            name="type"
            label="위젯 타입"
            notice={isEdit ? <Notice>* 위젯 복사, 편집 시에는 위젯 데이터 타입을 변경할 수 없습니다.</Notice> : null}
          >
            {isEdit ? (
              <span>{WIDGET_TYPE_LABEL_V2[WIDGET_TYPE_V2.EVENT]}</span>
            ) : (
              <Select list={WIDGET_TYPE_SELECT_LIST_V2} onChange={onChangeType} style={{ width: 140 }} />
            )}
          </Form.Item>
        </Panel.Section>
        <Panel.Section>
          <Form.Item name="layoutType" label="위젯 레이아웃 타입">
            <WidgetLayoutSelect type={WIDGET_TYPE_V2.EVENT} />
          </Form.Item>
        </Panel.Section>
        <Panel.Section>
          <Form.Item
            name="mainTitle"
            label="위젯 타이틀"
            rules={formRule.mainTitle.rules}
            normalize={textNormalize}
            notice={<Notice>* 위젯 타이틀의 글자 수는 최대 20자입니다.</Notice>}
          >
            <Input />
          </Form.Item>
        </Panel.Section>
        <Panel.Section>
          <Form.Item
            name="subTitle"
            label="위젯 서브 타이틀"
            rules={formRule.subTitle.rules}
            normalize={textNormalize}
            notice={<Notice>* 위젯 서브 타이틀의 글자 수는 최대 20자입니다.</Notice>}
          >
            <Input />
          </Form.Item>
        </Panel.Section>
        <Panel.Section>
          <Form.Item name="memo" label="메모" maxCount={1000} rules={formRule.memo.rules} normalize={textNormalize}>
            <TextArea />
          </Form.Item>
        </Panel.Section>
        <Panel.Section>
          <Form.Item label="위젯 노출기간" notice={<Notice>* 기획전 위젯은 무기한으로 설정됩니다.</Notice>}>
            <span>무기한</span>
          </Form.Item>
        </Panel.Section>
        <Panel.Section>
          <Form.Item label="노출되고 있는 페이지">
            <span>{initialValue?.currentExposurePage || '-'}</span>
          </Form.Item>
        </Panel.Section>
      </Panel>
      <Panel title="위젯에 노출되는 기획전 리스트">
        <Panel.Section hideIf={formRule.eventWidgetListTable.hideIf}>
          <EventWidgetListTable
            list={eventWidgetList}
            widgetItemList={widgetItemEvents || []}
            onDragEnd={handleEventListDragEnd}
          />
        </Panel.Section>
      </Panel>

      <Panel title="기획전 노출 정보를 결정하는 옵션">
        <Panel.Section>
          <Form.Item name="widgetItemEvents" rules={formRule.widgetItemEvents.rules}>
            <FormListTab
              max={30}
              notice={
                <Notice>
                  <p>* 드래그 앤 드랍으로 위젯 섹션의 순서를 변경할 수 있습니다.</p>
                </Notice>
              }
              initialTabItemValue={{
                event: null,
                mobileImage: [],
                pcImage: [],
                memo: '',
                exposurePeriod: getInitialPeriod(),
              }}
              onChangeActiveKey={setCurrentTab}
              activeKey={currentTab}
              items={(value: EventWidgetDetailV2['widgetItemEvents']) =>
                value.map((item, idx) => ({
                  id: item.id,
                  title: `${item.event?.mainTitle || '기획전'} - ${idx + 1}`,
                  content: (tabForm) => (
                    <>
                      <Panel.Section>
                        <Form.Item
                          name="event"
                          label="링크 ID"
                          required={true}
                          rules={formRule.widgetItemEvents.innerRules.event.rules}
                        >
                          <LinkItem
                            queryKey={WIDGET.EVENT_LIST}
                            columns={eventReactTableColumns}
                            fetcher={eventListFetcher}
                            onChange={(event) => {
                              tabForm.setFieldsValue({
                                exposurePeriod: {
                                  startDate:
                                    event.startDate ||
                                    dayjs(new Date().toISOString()).format(DATE_FORMAT.FULL_DASH_FORMAT),
                                  endDate:
                                    event.endDate ||
                                    dayjs(new Date().toISOString()).add(7, 'days').format(DATE_FORMAT.FULL_DASH_FORMAT),
                                },
                              });
                            }}
                            trigger={(openModal) => (
                              <SearchListTrigger
                                value={item.event}
                                triggerText="ID 선택"
                                linkText={`${item.event?.mainTitle}(${item.event?.id})`}
                                onClick={openModal}
                              />
                            )}
                          />
                        </Form.Item>
                      </Panel.Section>
                      <Panel.Section>
                        <Form.Item
                          name="exposurePeriod"
                          label="기획전 노출 기간"
                          notice={
                            <Notice>
                              <p>* 해당 기획전 위젯에 노출되는 기간입니다.</p>
                              <p>* 노출 기간은 기획전 활성 기간 내에 포함되어야 합니다.</p>
                            </Notice>
                          }
                        >
                          <DateRangePicker
                            format={DATE_FORMAT.FULL_DASH_FORMAT}
                            showTime={true}
                            min={item.event?.startDate}
                            max={item.event?.endDate}
                          />
                        </Form.Item>
                      </Panel.Section>
                      <Panel.Section>
                        <Form.Item
                          name="mobileImage"
                          label="App 이미지"
                          required={true}
                          rules={formRule.widgetItemEvents.innerRules.mobileImage.rules}
                          notice={
                            <Notice>
                              <p>* 각 배너 별 App 홈에 노출 될 이미지입니다.</p>
                            </Notice>
                          }
                        >
                          <ImageAndVideoUploadV2 modalTitle="APP 이미지" />
                        </Form.Item>
                      </Panel.Section>
                      <Panel.Section>
                        <Form.Item
                          name="pcImage"
                          label="PC 이미지"
                          required={true}
                          rules={formRule.widgetItemEvents.innerRules.pcImage.rules}
                          notice={
                            <Notice>
                              <p>* 각 배너 별 PC 홈에 노출 될 이미지입니다.</p>
                            </Notice>
                          }
                        >
                          <ImageAndVideoUploadV2 modalTitle="PC 이미지" />
                        </Form.Item>
                      </Panel.Section>
                      <Panel.Section>
                        <Form.Item
                          name="memo"
                          label="메모"
                          maxCount={250}
                          normalize={textNormalize}
                          rules={formRule.widgetItemEvents.innerRules.memo.rules}
                        >
                          <TextArea />
                        </Form.Item>
                      </Panel.Section>
                    </>
                  ),
                }))
              }
            />
          </Form.Item>
        </Panel.Section>
      </Panel>
      <Form.Item>
        <WidgetFormFooter
          typeLabel={WIDGET_TYPE_LABEL_V2[WIDGET_TYPE_V2.EVENT]}
          onClickPreview={() => setIsPreviewModalOpen(true)}
        />
      </Form.Item>
      <EventWidgetPreviewModal
        isOpen={isPreviewModalOpen}
        detail={form.getFieldsValue() as EventWidgetDetailV2}
        onClose={() => setIsPreviewModalOpen(false)}
      />
    </FormWithTab>
  );
};

export default Form.create<EventWidgetFormProps>()(EventWidgetForm);
